import { pull, LocalGameStorage, summarizeCollection, rarityLabel } from '@gachakit/core';
import type { CardData } from '@gachakit/core/types/card';
import type { PullResult } from '@gachakit/core/types/pack';
import { registerSW } from 'virtual:pwa-register';
import '../../../src/components/CardFace';
import '../../../src/components/Collection';
import '../../../src/components/DailyProgress';
import '../../../src/components/PackButton';
import '../../../src/components/PullResult';
import './app.css';
import { GBIFAdapter } from '../adapter';

type Screen = 'pull' | 'collection' | 'detail' | 'settings';
type BeforeInstallPromptChoice = 'accepted' | 'dismissed';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: BeforeInstallPromptChoice; platform: string }>;
  prompt: () => Promise<void>;
}

const PACK_SIZE = 5;

registerSW({ immediate: true });

const DAILY_LIMIT = Number(import.meta.env.VITE_DAILY_PULL_LIMIT ?? 10);
const PACK_LIMIT = Math.max(1, Math.floor(DAILY_LIMIT / PACK_SIZE));
const adapter = new GBIFAdapter();
const storage = new LocalGameStorage(adapter.id);

void adapter.prefetch(24);

let snapshot = storage.load();
let selectedCard: CardData | null = snapshot.lastResult?.card ?? snapshot.collection[0]?.card ?? null;
let revealPack: PullResult[] | null = null;
let activeScreen: Screen = 'pull';
let loading = false;
let resetPending = false;
let resetTimer: number | null = null;
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
let statusMessage = 'A sealed five-card field pack is ready for your binder.';

const app = document.querySelector<HTMLDivElement>('#app');

if (!app) {
  throw new Error('App root missing');
}

const appRoot = app;

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  deferredInstallPrompt = event as BeforeInstallPromptEvent;
  statusMessage = 'Species Gacha is ready to install as a standalone field binder.';
  render();
});

window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  statusMessage = 'Installed. The binder can now launch from your home screen.';
  render();
});

function render(): void {
  const summary = summarizeCollection(snapshot.collection);
  const remainingCards = Math.max(0, DAILY_LIMIT - snapshot.session.pullsToday);
  const usedPacks = Math.floor(snapshot.session.pullsToday / PACK_SIZE);
  const remainingPacks = Math.floor(remainingCards / PACK_SIZE);
  const lastCard = snapshot.lastResult?.card ?? null;
  const selectedEntry = selectedCard
    ? snapshot.collection.find((entry) => entry.card.id === selectedCard?.id) ?? null
    : null;
  const premiumOwned =
    summary.byRarity.rare + summary.byRarity.epic + summary.byRarity.legendary;
  const installLabel = deferredInstallPrompt ? 'Install App' : 'Install Unavailable';
  const resetLabel = resetPending ? 'Confirm Reset' : 'Reset Progress';
  const packNoun = remainingPacks === 1 ? 'pack' : 'packs';

  appRoot.innerHTML = `
    <div class="shell">
      <header class="app-hero">
        <div class="brand-mark" aria-hidden="true">
          <span></span>
        </div>
        <div class="hero-copy">
          <span class="eyebrow">Premium field binder</span>
          <h1 class="title">Species Gacha</h1>
          <p class="subtitle">Pull real GBIF species into a holo specimen collection built for your phone.</p>
        </div>
      </header>

      <section class="screen" data-screen="pull" data-active="${activeScreen === 'pull'}">
        <article class="vault">
          <div class="vault-head">
            <div>
              <span class="eyebrow">Daily vault</span>
              <h2>${remainingPacks > 0 ? `${remainingPacks} ${packNoun} left` : 'Vault locked'}</h2>
            </div>
            <span class="vault-ring" data-empty="${remainingPacks <= 0}">
              <strong>${remainingPacks}</strong>
              <small>${packNoun}</small>
            </span>
          </div>
          <p class="vault-status">${escapeHtml(statusMessage)}</p>
          <daily-progress></daily-progress>
          <div class="stat-grid">
            <div class="metric"><strong>${summary.uniqueCards}</strong><span>Unique</span></div>
            <div class="metric"><strong>${summary.totalCards}</strong><span>Total</span></div>
            <div class="metric"><strong>${premiumOwned}</strong><span>Rare+</span></div>
          </div>
          <pack-button></pack-button>
        </article>

        <article class="panel last-card" data-empty="${lastCard ? 'false' : 'true'}">
          <div class="section-header">
            <div>
              <h2 class="section-title">Latest specimen</h2>
              <p class="section-copy">${lastCard ? 'Recently filed in your binder.' : 'The first card will land here after a pack reveal.'}</p>
            </div>
          </div>
          ${
            lastCard
              ? '<gacha-card id="last-card" compact></gacha-card>'
              : '<div class="empty empty-vault"><span></span><strong>No specimens filed yet</strong><small>Open the sealed pack to start the binder.</small></div>'
          }
        </article>
      </section>

      <section class="screen" data-screen="collection" data-active="${activeScreen === 'collection'}">
        <article class="panel binder-panel">
          <div class="section-header">
            <div>
              <h2 class="section-title">Binder</h2>
              <p class="section-copy">Image-backed species cards, sorted by rarity and ready to inspect.</p>
            </div>
            <span class="chip">${summary.uniqueCards} unique</span>
          </div>
          <div class="rarity-strip" aria-label="Collection rarity counts">
            ${renderRarityCount('common', summary.byRarity.common)}
            ${renderRarityCount('uncommon', summary.byRarity.uncommon)}
            ${renderRarityCount('rare', summary.byRarity.rare)}
            ${renderRarityCount('epic', summary.byRarity.epic)}
            ${renderRarityCount('legendary', summary.byRarity.legendary)}
          </div>
          <gacha-collection></gacha-collection>
        </article>
      </section>

      <section class="screen" data-screen="detail" data-active="${activeScreen === 'detail'}">
        <article class="panel detail-panel">
          <div class="section-header">
            <div>
              <h2 class="section-title">${selectedCard ? escapeHtml(selectedCard.name) : 'Card detail'}</h2>
              <p class="section-copy">${
                selectedCard
                  ? `${escapeHtml(selectedCard.subtitle ?? 'Unknown specimen')} - filed ${selectedEntry?.count ?? 1}x`
                  : 'Select a card from the binder.'
              }</p>
            </div>
            ${selectedCard ? `<span class="chip" data-rarity="${selectedCard.rarity}">${rarityLabel(selectedCard.rarity)}</span>` : ''}
          </div>
          ${
            selectedCard
              ? `
                <gacha-card id="detail-card" showcase data-count="${selectedEntry?.count ?? 1}"></gacha-card>
                <div class="detail-actions">
                  <button class="button-secondary" id="back-to-binder" type="button">Back to Binder</button>
                  ${
                    selectedCard.sourceUrl
                      ? `<a class="button-secondary" href="${escapeHtml(selectedCard.sourceUrl)}" target="_blank" rel="noreferrer">GBIF Source</a>`
                      : ''
                  }
                </div>
              `
              : '<div class="empty empty-vault"><span></span><strong>No card selected</strong><small>Choose a specimen from the binder.</small></div>'
          }
        </article>
      </section>

      <section class="screen" data-screen="settings" data-active="${activeScreen === 'settings'}">
        <article class="panel settings-list">
          <div class="settings-card">
            <span class="settings-icon adapter-icon" aria-hidden="true"></span>
            <div>
              <strong>${escapeHtml(adapter.name)}</strong>
              <span>${escapeHtml(adapter.description)}</span>
            </div>
          </div>
          <div class="settings-card">
            <span class="settings-icon pack-icon" aria-hidden="true"></span>
            <div>
              <strong>Pack rules</strong>
              <span>${PACK_SIZE} cards per booster. Partial packs stay locked so each reveal feels complete.</span>
            </div>
          </div>
          <div class="settings-card">
            <span class="settings-icon storage-icon" aria-hidden="true"></span>
            <div>
              <strong>Local binder</strong>
              <span>Progress is stored on this device for the polished frontend v1.</span>
            </div>
          </div>
          <div class="actions">
            <button class="button-secondary" id="install-button" type="button" ${deferredInstallPrompt ? '' : 'disabled'}>${installLabel}</button>
            <button class="button-secondary danger" id="reset-button" type="button">${resetLabel}</button>
          </div>
        </article>
      </section>
    </div>

    <nav class="nav">
      ${renderNavButton('pull', 'Vault', activeScreen)}
      ${renderNavButton('collection', 'Binder', activeScreen)}
      ${renderNavButton('detail', 'Inspect', activeScreen)}
      ${renderNavButton('settings', 'Settings', activeScreen)}
    </nav>

    <pull-result></pull-result>
  `;

  const packButton = appRoot.querySelector('pack-button') as HTMLElement & {
    setState: (state: { loading: boolean; remaining: number }) => void;
  };
  packButton?.setState({ loading, remaining: remainingPacks });

  const progress = appRoot.querySelector('daily-progress') as HTMLElement & {
    setState: (state: { value: number; limit: number; label?: string; legendaryPity: number; epicPity: number }) => void;
  };
  progress?.setState({
    value: usedPacks,
    limit: PACK_LIMIT,
    label: 'packs',
    legendaryPity: snapshot.session.pullsSinceLegendary,
    epicPity: snapshot.session.pullsSinceEpic
  });

  const collection = appRoot.querySelector('gacha-collection') as HTMLElement & {
    items: typeof snapshot.collection;
  };
  if (collection) {
    collection.items = snapshot.collection;
  }

  const overlay = appRoot.querySelector('pull-result') as HTMLElement & {
    pack: PullResult[] | null;
  };
  overlay.pack = revealPack;

  const lastCardElement = appRoot.querySelector('#last-card') as (HTMLElement & { card: CardData | null }) | null;
  if (lastCardElement) {
    lastCardElement.setAttribute('data-count', String(snapshot.collection.find((entry) => entry.card.id === lastCard?.id)?.count ?? 1));
    lastCardElement.card = lastCard;
  }

  const detailCardElement = appRoot.querySelector('#detail-card') as (HTMLElement & { card: CardData | null }) | null;
  if (detailCardElement) {
    detailCardElement.card = selectedCard;
  }

  appRoot.querySelectorAll<HTMLButtonElement>('[data-screen-target]').forEach((button) => {
    button.addEventListener('click', () => {
      navigateTo(button.dataset.screenTarget as Screen);
    });
  });

  packButton?.addEventListener('press', handlePull, { once: true });
  collection?.addEventListener('select', handleCollectionSelect);
  collection?.addEventListener('open-pack', () => {
    navigateTo('pull');
  });
  overlay.addEventListener('close', () => {
    revealPack = null;
    render();
  });
  appRoot.querySelector('#back-to-binder')?.addEventListener('click', () => {
    navigateTo('collection');
  });
  appRoot.querySelector('#reset-button')?.addEventListener('click', handleReset);
  appRoot.querySelector('#install-button')?.addEventListener('click', () => {
    void handleInstall();
  });
}

async function handlePull(): Promise<void> {
  if (loading) {
    return;
  }

  if (snapshot.session.pullsToday + PACK_SIZE > DAILY_LIMIT) {
    statusMessage = 'Not enough room for another full pack today. Come back after local midnight.';
    render();
    return;
  }

  loading = true;
    statusMessage = 'Building your booster and warming the lead card image...';
  render();

  try {
    const results = await pullPack(PACK_SIZE);

    for (const result of results) {
      snapshot = storage.savePull(result);
    }

    selectedCard = results[results.length - 1]?.card ?? selectedCard;
    revealPack = results;
    activeScreen = 'pull';

    const newCount = results.filter((result) => result.isNew).length;
    statusMessage = `Opened a ${PACK_SIZE}-card booster. ${newCount} new ${newCount === 1 ? 'card' : 'cards'} added.`;
  } catch (error) {
    statusMessage =
      error instanceof Error && error.message === 'DAILY_LIMIT_REACHED'
        ? 'Daily limit reached. Come back after local midnight.'
        : 'Pack build failed. Check the network or try again.';
  } finally {
    loading = false;
    render();
  }
}

async function pullPack(size: number): Promise<PullResult[]> {
  let session = snapshot.session;
  const collectionIds = snapshot.collection.map((entry) => entry.card.id);
  const packIds: string[] = [];
  const results: PullResult[] = [];

  for (let index = 0; index < size; index += 1) {
    const result = await pull(adapter, session, collectionIds, DAILY_LIMIT, {
      excludeIds: packIds
    });

    results.push(result);
    session = result.session;
    packIds.push(result.card.id);

    if (!collectionIds.includes(result.card.id)) {
      collectionIds.push(result.card.id);
    }
  }

  return results;
}

function handleCollectionSelect(event: Event): void {
  const customEvent = event as CustomEvent<{ id?: string }>;
  const id = customEvent.detail?.id;
  if (!id) {
    return;
  }

  selectedCard = snapshot.collection.find((entry) => entry.card.id === id)?.card ?? null;
  navigateTo('detail');
}

function handleReset(): void {
  if (!resetPending) {
    resetPending = true;
    statusMessage = 'Tap confirm reset within five seconds to clear this local binder.';
    if (resetTimer !== null) {
      window.clearTimeout(resetTimer);
    }
    resetTimer = window.setTimeout(() => {
      resetPending = false;
      resetTimer = null;
      render();
    }, 5000);
    render();
    return;
  }

  if (resetTimer !== null) {
    window.clearTimeout(resetTimer);
    resetTimer = null;
  }
  resetPending = false;
  snapshot = storage.reset();
  selectedCard = null;
  revealPack = null;
  activeScreen = 'pull';
  statusMessage = 'Progress reset locally.';
  render();
}

function navigateTo(screen: Screen): void {
  activeScreen = screen;
  render();
  window.requestAnimationFrame(() => {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  });
}

async function handleInstall(): Promise<void> {
  if (!deferredInstallPrompt) {
    statusMessage = 'Install is not available in this browser session yet.';
    render();
    return;
  }

  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  await prompt.prompt();
  const choice = await prompt.userChoice;
  statusMessage =
    choice.outcome === 'accepted'
      ? 'Install accepted. Species Gacha is moving to your home screen.'
      : 'Install dismissed. You can keep playing in the browser.';
  render();
}

function renderRarityCount(rarity: CardData['rarity'], count: number): string {
  return `<span data-rarity="${rarity}"><strong>${count}</strong><small>${rarity}</small></span>`;
}

function renderNavButton(screen: Screen, label: string, current: Screen): string {
  return `
    <button class="nav-item" type="button" data-screen-target="${screen}" data-active="${current === screen}">
      <span class="nav-glyph ${screen}-glyph" aria-hidden="true"></span>
      <span>${label}</span>
    </button>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

render();
